import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Space, Tag, message, Popconfirm, Select, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CreateCoachModal } from '../CreateCoachModal';
import { CoachService } from '@/services/coach.service';
import apiService from '@/services/api.service';
import type { Coach, Club } from '@/types/models';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

const CoachesPage: React.FC = () => {
    const location = useLocation();
    const [coaches, setCoaches] = useState<Coach[]>([]);
    const [filteredCoaches, setFilteredCoaches] = useState<Coach[]>([]);
    const [clubs, setClubs] = useState<Club[]>([]);
    const [isCoachesLoading, setIsCoachesLoading] = useState(false);
    const [coachStatusFilter, setCoachStatusFilter] = useState<string>('all');
    const [coachClubFilter, setCoachClubFilter] = useState<number | null>(null);
    const [coachFederationFilter] = useState<number | null>(null);
    const [isCreateCoachModalVisible, setIsCreateCoachModalVisible] = useState(false);
    const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);

    const fetchCoaches = async () => {
        try {
            setIsCoachesLoading(true);
            const response = await CoachService.getAllCoaches();
            const coachesData = (response as any)?.data || response || [];
            setCoaches(Array.isArray(coachesData) ? coachesData : []);
            filterCoaches();
        } catch (error) {
            logger.error('Error fetching coaches:', error);
            message.error('Ошибка при загрузке списка тренеров');
        } finally {
            setIsCoachesLoading(false);
        }
    };

    const fetchClubs = async () => {
        try {
            const response = await apiService.get<ApiResponse<Club[]>>(API_ENDPOINTS.CLUBS.BASE);
            const clubsData = response.data?.data;
            let clubsArray: Club[] = [];
            if (Array.isArray(clubsData)) {
                clubsArray = clubsData;
            } else if (clubsData && typeof clubsData === 'object' && 'rows' in clubsData) {
                clubsArray = (clubsData as any).rows || [];
            }
            setClubs(clubsArray);
        } catch (error) {
            logger.error('Error fetching clubs:', error);
        }
    };

    const filterCoaches = () => {
        let filtered = [...coaches];
        if (coachStatusFilter !== 'all') {
            filtered = filtered.filter(coach => coach.status === coachStatusFilter);
        }
        if (coachClubFilter !== null) {
            filtered = filtered.filter(coach => coach.clubId === coachClubFilter);
        }
        if (coachFederationFilter !== null) {
            filtered = filtered.filter(coach => (coach.club as any)?.federation?.id === coachFederationFilter);
        }
        setFilteredCoaches(filtered);
    };

    useEffect(() => {
        fetchClubs();
    }, []);

    useEffect(() => {
        if (location.pathname === '/admin/coaches') {
            fetchCoaches();
        }
    }, [location.pathname]);

    useEffect(() => {
        filterCoaches();
    }, [coaches, coachStatusFilter, coachClubFilter, coachFederationFilter]);

    const coachColumns: ColumnsType<Coach> = [
        {
            title: 'ФИО',
            key: 'fullName',
            render: (_: unknown, record: Coach) => 
                `${record.lastName} ${record.firstName}${record.middleName ? ` ${record.middleName}` : ''}`,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по ФИО"
                        value={selectedKeys[0]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) => {
                const fullName = `${record.lastName} ${record.firstName}${record.middleName ? ` ${record.middleName}` : ''}`;
                return fullName.toLowerCase().includes(String(value).toLowerCase());
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по email"
                        value={selectedKeys[0]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.email?.toLowerCase().includes(String(value).toLowerCase()) ?? false,
        },
        {
            title: 'Клуб',
            key: 'club',
            render: (_: unknown, record: Coach) => record.club?.name || 'Не указан',
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Активен', value: 'active' },
                { text: 'Неактивен', value: 'inactive' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? 'Активен' : 'Неактивен'}
                </Tag>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: unknown, record: Coach) => (
                <Space>
                    <Button type="link" onClick={() => {
                        setSelectedCoach(record);
                        setIsCreateCoachModalVisible(true);
                    }}>
                        Редактировать
                    </Button>
                    <Popconfirm
                        title={record.status === 'active' ? 'Заблокировать тренера?' : 'Разблокировать тренера?'}
                        onConfirm={async () => {
                            try {
                                if (record.status === 'active') {
                                    await CoachService.blockCoach(record.id);
                                    message.success('Тренер успешно заблокирован');
                                } else {
                                    await CoachService.unblockCoach(record.id);
                                    message.success('Тренер успешно разблокирован');
                                }
                                fetchCoaches();
                            } catch (error) {
                                logger.error('Error changing coach status:', error);
                                message.error('Ошибка при изменении статуса тренера');
                            }
                        }}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="link" danger>
                            {record.status === 'active' ? 'Блокировать' : 'Разблокировать'}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setSelectedCoach(null);
                        setIsCreateCoachModalVisible(true);
                    }}
                >
                    Добавить тренера
                </Button>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <div>
                        <span style={{ marginRight: 8 }}>Статус:</span>
                        <Select
                            value={coachStatusFilter}
                            style={{ width: 120 }}
                            onChange={(value) => {
                                setCoachStatusFilter(value);
                                filterCoaches();
                            }}
                            options={[
                                { value: 'all', label: 'Все' },
                                { value: 'active', label: 'Активные' },
                                { value: 'inactive', label: 'Неактивные' },
                            ]}
                        />
                    </div>
                    <div>
                        <span style={{ marginRight: 8 }}>Клуб:</span>
                        <Select
                            value={coachClubFilter}
                            style={{ width: 200 }}
                            onChange={(value: string | number | null) => {
                                const clubId = (value === 'all' || value === null) ? null : (typeof value === 'number' ? value : Number(value));
                                setCoachClubFilter(clubId);
                                filterCoaches();
                            }}
                            allowClear
                            options={[
                                { value: 'all', label: 'Все клубы' },
                                ...clubs.map(club => ({
                                    value: club.id,
                                    label: club.name
                                }))
                            ]}
                        />
                    </div>
                </div>
            </div>
            <Table
                columns={coachColumns}
                dataSource={filteredCoaches}
                rowKey="id"
                loading={isCoachesLoading}
            />
            <CreateCoachModal
                visible={isCreateCoachModalVisible}
                coach={selectedCoach}
                onCancel={() => {
                    setIsCreateCoachModalVisible(false);
                    setSelectedCoach(null);
                }}
                onSuccess={() => {
                    setIsCreateCoachModalVisible(false);
                    setSelectedCoach(null);
                    fetchCoaches();
                }}
            />
        </div>
    );
};

export default CoachesPage;


